import { useCallback, useEffect, useState } from "react";
import { Loader2, Trash2, Globe, Rocket, Ban, Coins, Link2, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const fmtEur = (cents) => (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("de-DE") : "—");

const STATUS_STYLE = {
  checkout: "border-zinc-500/40 text-zinc-400 bg-zinc-500/5",
  in_arbeit: "border-goldlight/50 text-goldlight bg-goldlight/5",
  live: "border-emerald-600/40 text-emerald-400 bg-emerald-600/5",
  aktion_noetig: "border-orange-500/40 text-orange-400 bg-orange-500/5",
  gekuendigt: "border-red-500/40 text-red-400 bg-red-500/5",
};
const STATUS_LABEL = {
  checkout: "Zahlung offen",
  in_arbeit: "In Arbeit",
  live: "Live · Abo aktiv",
  aktion_noetig: "Kunde muss zahlen",
  gekuendigt: "Gekündigt",
};

export default function AdminWebsites() {
  const [orders, setOrders] = useState(undefined);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/website-orders");
      setOrders(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setOrders([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id, fn, successMsg) => {
    setBusy(id);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  const finish = (o) => {
    if (!window.confirm(`Website von ${o.customer_name} ist fertig und online?\n\nDas Abo über ${fmtEur(o.monthly_cents)}/Monat startet HEUTE mit der hinterlegten Karte. Der Kunde wird per E-Mail informiert.`)) return;
    act(o.id, async () => {
      const { data } = await api.post(`/website-orders/${o.id}/finish`);
      if (data.payment_action_needed) {
        toast.info("Abo angelegt — der Kunde muss die erste Abbuchung per Link bestätigen (E-Mail ist raus).");
      }
    }, "Website fertiggestellt — Abo gestartet, Kunde informiert.");
  };

  const cancel = (o) => {
    if (!window.confirm(`Abo von ${o.customer_name} (${o.number}) wirklich kündigen?\n\nEs werden keine weiteren monatlichen Beträge abgebucht. Der Kunde wird per E-Mail informiert.`)) return;
    act(o.id, () => api.post(`/website-orders/${o.id}/cancel`), "Abo gekündigt — Kunde informiert.");
  };

  const bookCredit = (o) => {
    const note = window.prompt(`Änderung für ${o.customer_name} verbuchen (${o.credits_remaining} von ${o.credits_total} Credits übrig).\n\nWas wurde geändert?`, "");
    if (note === null) return;
    act(o.id, () => api.post(`/website-orders/${o.id}/use-credit`, { note }), "Änderung verbucht.");
  };

  const saveUrl = (o) => {
    const url = window.prompt("Live-URL der Website (z. B. https://kundenseite.de):", o.website_url || "https://");
    if (url === null) return;
    act(o.id, () => api.patch(`/website-orders/${o.id}`, { website_url: url }), "Website-URL gespeichert.");
  };

  const remove = (o) => {
    if (!window.confirm(`Bestellung ${o.number} wirklich löschen?`)) return;
    act(o.id, () => api.delete(`/website-orders/${o.id}`), "Bestellung gelöscht.");
  };

  if (orders === undefined) {
    return (
      <div className="flex items-center gap-3 text-zinc-500 py-20 justify-center">
        <Loader2 size={18} className="animate-spin" /> Lade Website-Kunden ...
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="border border-dashed border-white/15 p-16 text-center" data-testid="websites-empty-state">
        <Globe size={28} className="mx-auto text-zinc-600 mb-4" />
        <p className="font-display text-xl text-zinc-400 mb-2">Noch keine Website-Kunden</p>
        <p className="text-sm text-zinc-600">Sobald jemand ein Website-Paket kauft und bezahlt, erscheint die Bestellung hier.</p>
      </div>
    );
  }

  return (
    <ul className="space-y-4" data-testid="websites-list">
      {orders.map((o) => (
        <li key={o.id} className={`border p-5 lg:p-6 ${o.express && o.status === "in_arbeit" ? "border-goldlight/60 bg-goldlight/[0.04]" : "border-white/10 bg-white/[0.02]"}`} data-testid={`website-order-${o.number}`}>
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div>
              <p className="font-display text-lg text-bone flex items-center gap-3 flex-wrap">
                {o.number}
                {o.express && (
                  <span className="inline-flex items-center gap-1.5 bg-goldlight text-coal px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em]" data-testid={`website-express-${o.number}`}>
                    <Zap size={11} /> Express
                  </span>
                )}
              </p>
              <p className="text-xs text-zinc-500 mt-1">Gekauft am {fmtDate(o.created_at)}</p>
              {o.express && o.delivery_deadline && o.status === "in_arbeit" && (
                <p className="text-xs text-goldlight mt-1" data-testid={`website-deadline-${o.number}`}>
                  Express-Frist bis: {new Date(o.delivery_deadline).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })} Uhr
                </p>
              )}
            </div>
            <span className={`border px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] ${STATUS_STYLE[o.status] || STATUS_STYLE.checkout}`} data-testid={`website-status-${o.number}`}>
              {STATUS_LABEL[o.status] || o.status}
            </span>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Kunde</p>
              <p className="text-zinc-200">{o.customer_name}</p>
              <p className="text-zinc-500 text-xs">{o.customer_email}{o.phone ? ` · ${o.phone}` : ""}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Paket</p>
              <p className="text-zinc-200">Website {o.package_label}</p>
              <p className="text-zinc-500 text-xs">
                {fmtEur(o.total_price_cents)} einmalig{o.express ? " (inkl. Express)" : ""} · bezahlt {fmtDate(o.paid_at)}
              </p>
              {o.website_url && (
                <a href={o.website_url} target="_blank" rel="noopener noreferrer" className="text-xs text-goldlight hover:underline flex items-center gap-1 mt-1">
                  <Link2 size={11} /> {o.website_url}
                </a>
              )}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Abo monatlich</p>
              <p className="font-display text-xl text-goldlight" data-testid={`website-monthly-${o.number}`}>{fmtEur(o.monthly_cents)}</p>
              <p className="text-zinc-500 text-xs">10 € Betrieb{o.support_tier !== "none" ? ` + ${o.support_label}` : ""}</p>
              {o.has_subscription && (
                <p className="text-xs mt-1">
                  <span className="text-zinc-500">Nächste Zahlung:</span>{" "}
                  <span className="text-zinc-300" data-testid={`website-next-payment-${o.number}`}>{fmtDate(o.next_payment_at)}</span>
                </p>
              )}
              {o.failed_payments > 0 && (
                <p className="text-xs text-red-400 mt-1" data-testid={`website-failed-${o.number}`}>
                  {o.failed_payments}× Zahlung fehlgeschlagen
                </p>
              )}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Änderungs-Credits</p>
              {o.credits_total > 0 ? (
                <>
                  <p className="text-zinc-200" data-testid={`website-credits-${o.number}`}>
                    {o.credits_used} / {o.credits_total} verwendet
                  </p>
                  <p className="text-zinc-500 text-xs">Reset bei jeder Monatszahlung</p>
                </>
              ) : (
                <p className="text-zinc-500 text-xs">Kein Support-Abo</p>
              )}
              {o.last_payment_at && (
                <p className="text-xs text-zinc-500 mt-2">Letzte Zahlung: {fmtDate(o.last_payment_at)}</p>
              )}
            </div>
          </div>

          {o.notes && (
            <p className="mt-4 pt-4 border-t border-white/5 text-sm text-zinc-400">
              <span className="text-zinc-500">Notizen:</span> {o.notes}
            </p>
          )}

          <div className="flex flex-wrap gap-3 mt-5">
            {o.status === "in_arbeit" && (
              <button
                onClick={() => finish(o)}
                disabled={busy === o.id}
                data-testid={`website-finish-${o.id}`}
                className="text-[11px] uppercase tracking-[0.2em] text-coal bg-goldlight px-4 py-2 transition-colors duration-300 hover:bg-gold flex items-center gap-2 disabled:opacity-50"
              >
                {busy === o.id ? <Loader2 size={13} className="animate-spin" /> : <Rocket size={13} />}
                Website fertig — Abo starten
              </button>
            )}
            {(o.status === "live" || o.status === "aktion_noetig") && (
              <button
                onClick={() => cancel(o)}
                disabled={busy === o.id}
                data-testid={`website-cancel-${o.id}`}
                className="text-[11px] uppercase tracking-[0.2em] text-orange-400 border border-orange-500/40 px-4 py-2 transition-colors duration-300 hover:bg-orange-500 hover:text-white flex items-center gap-2 disabled:opacity-50"
              >
                <Ban size={13} /> Abo kündigen
              </button>
            )}
            {o.credits_total > 0 && o.status === "live" && (
              <button
                onClick={() => bookCredit(o)}
                disabled={busy === o.id || o.credits_remaining === 0}
                data-testid={`website-use-credit-${o.id}`}
                className="text-[11px] uppercase tracking-[0.2em] text-goldlight border border-goldlight/40 px-4 py-2 transition-colors duration-300 hover:bg-goldlight hover:text-coal flex items-center gap-2 disabled:opacity-40"
              >
                <Coins size={13} /> Änderung verbuchen
              </button>
            )}
            <button
              onClick={() => saveUrl(o)}
              disabled={busy === o.id}
              data-testid={`website-set-url-${o.id}`}
              className="text-[11px] uppercase tracking-[0.2em] text-zinc-400 border border-white/10 px-4 py-2 transition-colors duration-300 hover:border-goldlight/50 hover:text-goldlight"
            >
              Website-URL
            </button>
            {(!o.has_subscription || o.status === "gekuendigt") && (
              <button
                onClick={() => remove(o)}
                disabled={busy === o.id}
                data-testid={`website-delete-${o.id}`}
                className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 hover:text-red-400 border border-white/10 hover:border-red-500/40 px-4 py-2 transition-colors duration-300 flex items-center gap-2"
              >
                <Trash2 size={13} /> Löschen
              </button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
